import type { ReactNode } from 'react'

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { cn } from '@/lib/utils'

import { formatServiceType } from './connector-format.util'
import { ConnectorProviderCardContent } from './connector-provider-card-content.atom'
import { ConnectorProviderIcon } from './connector-provider-icon.atom'
import { ConnectorStatusBadge } from './connector-status-badge.atom'

export interface ConnectorProviderCardProps {
  providerSlug: string
  providerDisplayName: string
  isConnected: boolean
  connectedAccountEmail: string | null
  serviceTypeList: string[]
  isConnecting: boolean
  onConnect: (providerSlug: string) => void
  onDisconnect: (providerSlug: string) => void
  onClick?: () => void
  className?: string
}

/**
 * Card displaying a single connector provider with connection status and actions.
 * Shows provider name, available services, and connect/disconnect controls.
 */
export const ConnectorProviderCard = ({
  providerSlug,
  providerDisplayName,
  isConnected,
  connectedAccountEmail,
  serviceTypeList,
  isConnecting,
  onConnect,
  onDisconnect,
  onClick,
  className,
}: ConnectorProviderCardProps): ReactNode => (
  <Card
    className={cn(
      'connector-provider-card',
      onClick && 'cursor-pointer transition-colors hover:border-primary/50',
      className
    )}
    onClick={onClick}
  >
    <CardHeader>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ConnectorProviderIcon providerSlug={providerSlug} size="md" />
          <div>
            <CardTitle className="text-base">{providerDisplayName}</CardTitle>
            <CardDescription className="text-xs">
              {serviceTypeList.map(formatServiceType).join(', ')}
            </CardDescription>
          </div>
        </div>
        <ConnectorStatusBadge isConnected={isConnected} />
      </div>
    </CardHeader>
    <CardContent>
      <ConnectorProviderCardContent
        providerSlug={providerSlug}
        providerDisplayName={providerDisplayName}
        isConnected={isConnected}
        connectedAccountEmail={connectedAccountEmail}
        isConnecting={isConnecting}
        onConnect={onConnect}
        onDisconnect={onDisconnect}
      />
    </CardContent>
  </Card>
)

ConnectorProviderCard.displayName = 'ConnectorProviderCard'
